/**
 * webPush.js — клиент Web Push для тикет-системы Рублокса.
 *
 * Поток:
 *   1. supported() — проверить что браузер поддерживает Push API.
 *   2. enable() — попросить permission, зарегистрировать SW,
 *      подписаться, отправить subscription на бэк.
 *   3. disable() — отписаться + сказать бэку удалить запись.
 *
 * Все функции возвращают Promise<{ok, error?, reason?}>.
 */
import { request, getToken } from "./client";

const SW_PATH = "/sw-push.js";

export function supported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permission() {
  if (!supported()) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** Достать публичный VAPID-ключ с бэка и привести в Uint8Array. */
async function getVapidKey() {
  const data = await request(
    "/api-storys/kubikon3d/tickets/web-push/public-key"
  );
  if (!data.public_key) throw new Error("vapid_not_configured");
  return urlBase64ToUint8Array(data.public_key);
}

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enable() {
  if (!supported()) return { ok: false, reason: "unsupported" };
  if (!getToken())  return { ok: false, reason: "not_logged_in" };

  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return { ok: false, reason: "denied" };

  let reg;
  try {
    reg = await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
    // Дожидаемся active worker'а — иначе pushManager.subscribe бросит.
    if (!reg.active) {
      await new Promise((resolve) => {
        const tryActive = () => {
          if (reg.active) return resolve();
          setTimeout(tryActive, 100);
        };
        tryActive();
      });
    }
  } catch (e) {
    return { ok: false, reason: "sw_register_failed", error: e.message };
  }

  let sub;
  try {
    const key = await getVapidKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });
  } catch (e) {
    return { ok: false, reason: "subscribe_failed", error: e.message };
  }

  try {
    const body = sub.toJSON();
    await request("/api-storys/kubikon3d/tickets/web-push/subscribe", {
      method: "POST",
      auth: true,
      body: {
        endpoint: body.endpoint,
        keys: body.keys,
      },
    });
    return { ok: true };
  } catch (e) {
    // На бэк не дошло — отписываем чтобы не висела «зомби»-подписка.
    try { await sub.unsubscribe(); } catch {}
    return { ok: false, reason: "backend_failed", error: e.message };
  }
}

export async function disable() {
  if (!supported()) return { ok: true };
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return { ok: true };
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      try {
        await request("/api-storys/kubikon3d/tickets/web-push/unsubscribe", {
          method: "POST",
          auth: true,
          body: { endpoint },
        });
      } catch { /* всё равно отписываемся ниже */ }
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

/** Текущая подписка (или null) — для отображения состояния в UI. */
export async function currentSubscription() {
  if (!supported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}
